import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FieldWrapper, QuestionText, RadioGroup, RadioLabel, RadioLabelText, Radio } from './styles';

type RadioOption = {
  label: string;
  value: boolean | string;
};

type CustomRadioProps = {
  question: string;
  name: string;
  options: RadioOption[];
  defaultValue?: boolean | string;
};

type BooleanRadioProps = {
  question: string;
  name: string;
  defaultValue?: boolean;
};

// Generic radio component with custom options
export const CustomRadio = ({ question, name, options, defaultValue }: CustomRadioProps): JSX.Element => {
  const { control, formState } = useFormContext();
  const hasError = !!formState.errors[name];

  return (
    <FieldWrapper>
      <QuestionText>{question}</QuestionText>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ value, onChange }) => (
          <RadioGroup>
            {options.map((option) => (
              <RadioLabel
                key={option.value.toString()}
                selected={value?.toString() === option.value.toString()}
                hasError={hasError}
                onClick={(e) => {
                  e.preventDefault();
                  onChange(option.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                  }
                }}
                tabIndex={0}
                role='button'
              >
                <RadioLabelText>{option.label}</RadioLabelText>
                <Radio
                  name={name}
                  value={option.value.toString()}
                  checked={value?.toString() === option.value.toString()}
                />
              </RadioLabel>
            ))}
          </RadioGroup>
        )}
      />
    </FieldWrapper>
  );
};

// Boolean radio component (Yes/No)
export const BooleanRadio = ({ question, name, defaultValue = false }: BooleanRadioProps): JSX.Element => (
  <CustomRadio
    question={question}
    name={name}
    defaultValue={defaultValue}
    options={[
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ]}
  />
);
